import { useEffect, useState } from 'react';

export type CourseFormat = 'online' | 'offline';
export type CourseType = 'individual' | 'closed_group' | 'group';
export type BillingUnit = 'month' | 'course';

export interface PricingOption {
  format: CourseFormat;
  type: CourseType;
  price: number;
}

export interface CourseData {
  id: string;
  title: string;
  duration: string;
  frequency: string;
  description: string;
  level?: string;
  icon: string;
  color: string;
  pricing: PricingOption[];
  specialOffer?: {
    monthlyPayment: number;
    totalMonthly: number;
    totalUpfront: number;
    discount: number;
  };
  durationMonths: number;
  paymentMonths: number;
  billingUnit: BillingUnit;
  upfrontDiscount: number;
  isBootcamp?: boolean;
  prerequisites?: string;
  timeSlots?: string[];
  weekends?: string;
  trainingType?: string;
}

export interface WebsiteCatalog {
  siteId: string;
  domain: string;
  revision: number;
  updatedAt: string | null;
  courses: CourseData[];
}

interface PricingPayload {
  format: CourseFormat;
  type: CourseType;
  price: number;
}

interface CoursePricingPayload {
  id: string;
  title: string;
  trainingType?: string;
  durationMonths: number;
  paymentMonths: number;
  billingUnit: BillingUnit;
  upfrontDiscount: number;
  pricing: PricingPayload[];
}

interface WebsiteCatalogPayload {
  siteId: string;
  domain: string;
  revision: number;
  updatedAt: string | null;
  courses: CoursePricingPayload[];
}

const WEBSITE_SITE_ID = 'itansar';

const COURSE_META_DATA: Omit<CourseData, 'pricing'>[] = [
  { id: 'python-standard', title: 'Python Программирование', duration: '12 месяцев', durationMonths: 12, paymentMonths: 6, billingUnit: 'month', upfrontDiscount: 10000, frequency: '2 занятия в неделю', description: 'Углублённое изучение Python с нуля до создания реальных проектов. Первый курс разделён на 2 этапа по 6 месяцев', trainingType: 'СТАНДАРТ', icon: 'python', color: '#F5E6FE' },
  { id: 'python-intensive', title: 'Python Программирование', duration: '6 месяцев', durationMonths: 6, paymentMonths: 6, billingUnit: 'month', upfrontDiscount: 10000, frequency: '5 занятий в неделю', description: 'Максимально интенсивный курс для быстрого старта в программировании на Python. Курс разделён на 2 этапа по 3 месяца', trainingType: 'УСКОРЕННОЕ ОБУЧЕНИЕ', icon: 'python', color: '#F5E6FE' },
  { id: 'javascript-standard', title: 'JavaScript Программирование', duration: '12 месяцев', durationMonths: 12, paymentMonths: 6, billingUnit: 'month', upfrontDiscount: 10000, frequency: '2 занятия в неделю', description: 'Изучение JavaScript с нуля до создания современных веб-приложений. Первый курс разделён на 2 этапа по 6 месяцев', trainingType: 'СТАНДАРТ', icon: 'javascript', color: '#FFF9DD' },
  { id: 'javascript-intensive', title: 'JavaScript Программирование', duration: '6 месяцев', durationMonths: 6, paymentMonths: 6, billingUnit: 'month', upfrontDiscount: 10000, frequency: '5 занятий в неделю', description: 'Интенсивное изучение JavaScript для быстрого старта в веб-разработке. Курс разделён на 2 этапа по 3 месяца', trainingType: 'УСКОРЕННОЕ ОБУЧЕНИЕ', icon: 'javascript', color: '#FFF9DD' },
  { id: 'computer-literacy', title: 'Компьютерная грамотность', duration: '1 месяц', durationMonths: 1, paymentMonths: 1, billingUnit: 'month', upfrontDiscount: 10000, frequency: '2 раза в неделю', description: 'Основы работы с компьютером для начинающих пользователей', icon: 'p4', color: '#E8F4FD' },
  { id: 'bootcamp', title: 'Буткемп (Сатисар)', duration: '5 месяцев', durationMonths: 5, paymentMonths: 1, billingUnit: 'course', upfrontDiscount: 0, frequency: '5 занятий в неделю', description: 'Интенсивная программа полного погружения в программирование на Python', level: 'с базой (знание основ Python)', icon: 'p3', color: '#fdcbd6', timeSlots: ['09:00-15:00', '15:00-21:00'], weekends: 'суббота, воскресенье', isBootcamp: true, prerequisites: 'знание основ Python' },
];

export const FORMAT_LABELS: Record<CourseFormat, string> = { online: 'Онлайн', offline: 'Оффлайн' };
export const TYPE_LABELS: Record<CourseType, string> = { individual: 'Индивидуально', closed_group: 'Закрытая группа', group: 'Группа' };
const COURSE_META_BY_ID = new Map(COURSE_META_DATA.map(course => [course.id, course]));
const STANDARD_PRICING_KEYS = ['online:individual', 'online:closed_group', 'online:group', 'offline:individual', 'offline:closed_group', 'offline:group'];
const BOOTCAMP_PRICING_KEYS = ['online:group', 'offline:group'];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const isPositiveInteger = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value > 0;
const isNonNegativeInteger = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isFormat = (value: unknown): value is CourseFormat => value === 'online' || value === 'offline';
const isType = (value: unknown): value is CourseType => value === 'individual' || value === 'closed_group' || value === 'group';
const isBillingUnit = (value: unknown): value is BillingUnit => value === 'month' || value === 'course';

const isPricing = (value: unknown): value is PricingPayload => {
  if (!isRecord(value)) return false;
  return isFormat(value.format) && isType(value.type) && isPositiveInteger(value.price) && value.price <= 10_000_000;
};

const isCoursePricing = (value: unknown): value is CoursePricingPayload => {
  if (!isRecord(value)) return false;
  return typeof value.id === 'string' && typeof value.title === 'string' && value.title.trim().length > 0 &&
    (value.trainingType === undefined || typeof value.trainingType === 'string') &&
    isPositiveInteger(value.durationMonths) && isPositiveInteger(value.paymentMonths) &&
    isBillingUnit(value.billingUnit) && isNonNegativeInteger(value.upfrontDiscount) &&
    Array.isArray(value.pricing) && value.pricing.every(isPricing);
};

export const parseWebsiteCatalog = (value: unknown, expectedSiteId = WEBSITE_SITE_ID): WebsiteCatalog | null => {
  if (!isRecord(value) || value.siteId !== expectedSiteId || typeof value.domain !== 'string' || value.domain.trim().length === 0 ||
    !isPositiveInteger(value.revision) || !(value.updatedAt === null || typeof value.updatedAt === 'string') ||
    !Array.isArray(value.courses) || value.courses.length !== COURSE_META_DATA.length || !value.courses.every(isCoursePricing)) return null;

  const payload = value as unknown as WebsiteCatalogPayload;
  const byId = new Map(payload.courses.map(course => [course.id, course]));
  if (byId.size !== COURSE_META_DATA.length) return null;
  for (const remote of payload.courses) {
    const meta = COURSE_META_BY_ID.get(remote.id);
    const expectedPricingKeys = remote.id === 'bootcamp' ? BOOTCAMP_PRICING_KEYS : STANDARD_PRICING_KEYS;
    const pricingKeys = remote.pricing.map(option => `${option.format}:${option.type}`);
    const isExactPricing = pricingKeys.length === expectedPricingKeys.length &&
      new Set(pricingKeys).size === expectedPricingKeys.length &&
      expectedPricingKeys.every(key => pricingKeys.includes(key));
    const minimumTotal = Math.min(...remote.pricing.map(option => option.price)) * remote.paymentMonths;
    if (!meta || remote.billingUnit !== meta.billingUnit || remote.paymentMonths > remote.durationMonths ||
      !isExactPricing || remote.upfrontDiscount >= minimumTotal ||
      (remote.id === 'bootcamp' && (remote.paymentMonths !== 1 || remote.upfrontDiscount !== 0))) return null;
  }
  const courses = COURSE_META_DATA.flatMap(meta => {
    const remote = byId.get(meta.id);
    if (!remote) return [];
    return [{
      ...meta,
      trainingType: remote.trainingType ?? meta.trainingType,
      durationMonths: remote.durationMonths,
      paymentMonths: remote.paymentMonths,
      billingUnit: remote.billingUnit,
      upfrontDiscount: remote.upfrontDiscount,
      pricing: remote.pricing,
    }];
  });

  return { siteId: payload.siteId, domain: payload.domain, revision: payload.revision, updatedAt: payload.updatedAt, courses };
};

export const calculatePricingDetails = (course: CourseData, pricing: PricingOption) => {
  const total = pricing.price * course.paymentMonths;
  return {
    paymentMonths: course.paymentMonths,
    billingUnit: course.billingUnit,
    discount: course.upfrontDiscount,
    total,
    totalUpfront: total - course.upfrontDiscount,
  };
};

export const formatPaymentPeriod = (months: number) => `${months} ${months === 1 ? 'месяц' : months < 5 ? 'месяца' : 'месяцев'}`;

type CatalogState = { status: 'loading' | 'ready' | 'error'; catalog: WebsiteCatalog | null; error: string | null; refreshing: boolean };
let state: CatalogState = { status: 'loading', catalog: null, error: null, refreshing: false };
const listeners = new Set<(next: CatalogState) => void>();
let request: AbortController | null = null;
let interval: number | null = null;
let lastRequestAt = 0;

const publish = (next: CatalogState) => { state = next; listeners.forEach(listener => listener(next)); };
const apiUrl = () => `${(process.env.REACT_APP_WEBSITE_PRICING_API_URL || 'https://id.ansarium.ru/api/public/website-pricing').replace(/\/$/, '')}/${WEBSITE_SITE_ID}`;

export const loadWebsiteCatalog = async (force = false) => {
  if (request) return;
  if (!force && state.status === 'ready' && !state.refreshing) return;
  const activeRequest = new AbortController();
  request = activeRequest;
  lastRequestAt = Date.now();
  publish(state.catalog && state.status === 'ready'
    ? { status: 'ready', catalog: state.catalog, error: null, refreshing: true }
    : { status: 'loading', catalog: state.catalog, error: null, refreshing: Boolean(state.catalog) });
  const isCurrentRequest = () => request === activeRequest && !activeRequest.signal.aborted;
  try {
    const response = await fetch(apiUrl(), { signal: activeRequest.signal });
    const payload: unknown = response.ok ? await response.json() : null;
    const catalog = parseWebsiteCatalog(payload);
    if (!catalog) throw new Error('invalid pricing catalog');
    if (isCurrentRequest()) publish({ status: 'ready', catalog, error: null, refreshing: false });
  } catch (error) {
    if (isCurrentRequest() && (error as { name?: string }).name !== 'AbortError') {
      publish({ status: 'error', catalog: state.catalog, error: 'Не удалось загрузить цены', refreshing: false });
    }
  } finally {
    if (request === activeRequest) request = null;
  }
};

export const getWebsiteCatalogState = () => state;

export const disposeWebsiteCatalog = () => {
  request?.abort();
  request = null;
  if (state.refreshing) publish({ ...state, refreshing: false });
};

const refreshWhenDue = () => { if (Date.now() - lastRequestAt >= 60_000) void loadWebsiteCatalog(true); };

export const useWebsiteCatalog = () => {
  const [snapshot, setSnapshot] = useState<CatalogState>(state);
  useEffect(() => {
    listeners.add(setSnapshot);
    void loadWebsiteCatalog();
    const onFocus = () => refreshWhenDue();
    window.addEventListener('focus', onFocus);
    if (!interval) interval = window.setInterval(refreshWhenDue, 60_000);
    return () => {
      listeners.delete(setSnapshot);
      window.removeEventListener('focus', onFocus);
      if (!listeners.size) {
        if (interval) window.clearInterval(interval);
        interval = null;
        disposeWebsiteCatalog();
      }
    };
  }, []);
  return { ...snapshot, retry: () => void loadWebsiteCatalog(true) };
};
